import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const COPY = {
  cs: {
    eyebrow: "O nás",
    badge: "Rodinná česká firma · od 1998",
    title: "Budoucnost retailu. Stavíme ji v ČR i SR.",
    lead:
      "Global Marketing je rodinná česká firma založená v roce 1998. Prostřednictvím vlastních software a datových platforem pro sběr, zpracování a reporting instore dat realizujeme ročně desítky projektů pro naše zákazníky. Pomáháme značkám rozhodovat se na základě dat, ne dojmů.",
    body:
      "Více než dvě dekády stavíme tým merchandiserů, regionálních manažerů a datových inženýrů, kteří fungují jako jeden organismus. Naše vlastní mobilní aplikace MERVIK, datový systém SYMES i světová analytická platforma QlikView znamenají, že nejsme závislí na externích nástrojích — a můžeme proto klientům poskytovat data z terénu dostupná v reálném čase.",
    stats: [
      { k: "1998", v: "rok založení · rodinná firma" },
      { k: "1 200+", v: "monitorovaných prodejen" },
      { k: "CZ + SK", v: "plné pokrytí maloobchodu" },
      { k: "40 mil.+", v: "datových bodů ročně" },
    ],
    why: {
      eyebrow: "Proč Global Marketing",
      title: "28 let na trhu. Stejní lidé. Stabilní základna klientů. Dynamický vývoj inovací.",
      intro:
        "Global Marketing byl založen v roce 1998 jako rodinná firma a dodnes ji vede její zakladatel. Nejstarší aktivní klienti spolupracují s agenturou od roku 2005.",
      points: [
        "Personální stabilita na úrovni manažerů i merchandiserů — vaše prodejny servisují lidé, kteří je znají.",
        "Osobní přístup, projekty na míru.",
        "Vysoké inovativní tempo — každý rok nové projekty a produkty.",
        "Vlastní softwarové systémy SYMES, MERVIK a licence QlikView — nejsme závislí na externích nástrojích a můžeme je přizpůsobit přesně vašim potřebám.",
      ],
    },
    valuesT: "Co u nás platí.",
    values: [
      { h: "Fakta nad domněnkami", t: "Nepracujeme s odhady. Pracujeme s tím, co je doložené v terénu — fotkou či datovým záznamem." },
      { h: "Vlastní technologie", t: "MERVIK, SYMES a QlikView — nepoužíváme cizí řešení. Všechny naše softwarové a datové produkty pocházejí z dílny našich vývojářů." },
      { h: "Měřitelnost a SLA", t: "Každá služba má KPI a smluvní rámec. Kvalita není slib, je to měřitelný výstup." },
      { h: "Rychlost", t: "Reklamaci řešíme ten samý den. Reporty máte k dispozici online včetně kompletní sumarizace druhý den v 6:00 ráno." },
    ],
    ctaTitle: "Pojďme se potkat.",
    ctaSub: "Připravíme nezávazný odhad pro váš konkrétní rozsah — řetězce, počet prodejen, frekvence návštěv.",
    ctaButton: "Kontaktujte nás",
  },
  en: {
    eyebrow: "About",
    badge: "Czech family business · since 1998",
    title: "Future of retail. Built across CZ and SK.",
    lead:
      "Global Marketing is a Czech family business founded in 1998. Through our own software and data platforms for collecting, processing and reporting instore data, we deliver dozens of projects each year. We help brands decide on data — not impressions.",
    body:
      "For over two decades we've built a team of merchandisers, regional managers and data engineers that operate as a single organism. Our own MERVIK mobile app, SYMES data system and the QlikView analytics platform mean we don't depend on external tooling — and we can deliver field data in real time.",
    stats: [
      { k: "1998", v: "founded · family-owned" },
      { k: "1,200+", v: "monitored stores" },
      { k: "CZ + SK", v: "full retail coverage" },
      { k: "40M+", v: "datapoints / year" },
    ],
    why: {
      eyebrow: "Why Global Marketing",
      title: "28 years on the market. Same people. A stable client base. Dynamic innovation.",
      intro:
        "Global Marketing was founded in 1998 as a family business and is still led by its founder. Our longest-standing active clients have been with us since 2005.",
      points: [
        "Stability at every level — managers and merchandisers — your stores are serviced by people who know them.",
        "Personal approach, projects designed to fit.",
        "High innovation cadence — new projects and products every year.",
        "Proprietary software (SYMES, MERVIK) and a QlikView licence — no vendor lock-in, fully tailored to your needs.",
      ],
    },
    valuesT: "What stands here.",
    values: [
      { h: "Facts over assumptions", t: "We don't work with estimates. We work with what's documented in the field — photo or data record." },
      { h: "Own technology", t: "MERVIK, SYMES and QlikView — no third-party boxes. All our software and data products come from our own developers." },
      { h: "Measurability and SLA", t: "Every service has KPIs and a contractual frame. Quality isn't a promise, it's a measured output." },
      { h: "Speed", t: "We resolve issues same-day. Reports available online with a full summary the next day at 6:00 a.m." },
    ],
    ctaTitle: "Let's meet.",
    ctaSub: "We'll prepare a non-binding estimate for your specific scope — chains, store count, visit frequency.",
    ctaButton: "Contact us",
  },
  sk: {
    eyebrow: "O nás",
    badge: "Rodinná česká firma · od 1998",
    title: "Budúcnosť retailu. Staviame ju v ČR aj SR.",
    lead:
      "Global Marketing je rodinná česká firma založená v roku 1998. Prostredníctvom vlastných softvérov a dátových platforiem na zber, spracovanie a reporting instore dát realizujeme ročne desiatky projektov pre našich zákazníkov. Pomáhame značkám rozhodovať sa na základe dát, nie dojmov.",
    body:
      "Viac ako dve dekády budujeme tím merchandiserov, regionálnych manažérov a dátových inžinierov, ktorí fungujú ako jeden organizmus. Naša vlastná mobilná aplikácia MERVIK, dátový systém SYMES aj svetová analytická platforma QlikView znamenajú, že nie sme závislí od externých nástrojov — a môžeme klientom poskytovať dáta z terénu v reálnom čase.",
    stats: [
      { k: "1998", v: "rok založenia · rodinná firma" },
      { k: "1 200+", v: "monitorovaných predajní" },
      { k: "CZ + SK", v: "plné pokrytie maloobchodu" },
      { k: "40 mil.+", v: "dátových bodov ročne" },
    ],
    why: {
      eyebrow: "Prečo Global Marketing",
      title: "28 rokov na trhu. Tí istí ľudia. Stabilná základňa klientov. Dynamický vývoj inovácií.",
      intro:
        "Global Marketing bol založený v roku 1998 ako rodinná firma a dodnes ju vedie jej zakladateľ. Najstarší aktívni klienti spolupracujú s agentúrou od roku 2005.",
      points: [
        "Personálna stabilita na úrovni manažérov aj merchandiserov — vaše predajne servisujú ľudia, ktorí ich poznajú.",
        "Osobný prístup, projekty na mieru.",
        "Vysoké inovatívne tempo — každý rok nové projekty a produkty.",
        "Vlastné softvérové systémy SYMES, MERVIK a licencia QlikView — nie sme závislí od externých nástrojov a môžeme ich prispôsobiť presne vašim potrebám.",
      ],
    },
    valuesT: "Čo u nás platí.",
    values: [
      { h: "Fakty nad domnienkami", t: "Nepracujeme s odhadmi. Pracujeme s tým, čo je doložené v teréne — fotkou alebo dátovým záznamom." },
      { h: "Vlastná technológia", t: "MERVIK, SYMES a QlikView — nepoužívame cudzie riešenia. Všetky naše softvérové a dátové produkty pochádzajú z dielne našich vývojárov." },
      { h: "Merateľnosť a SLA", t: "Každá služba má KPI a zmluvný rámec. Kvalita nie je sľub, je to merateľný výstup." },
      { h: "Rýchlosť", t: "Reklamáciu riešime v ten istý deň. Reporty máte k dispozícii online vrátane úplnej sumarizácie nasledujúci deň o 6:00 ráno." },
    ],
    ctaTitle: "Poďme sa stretnúť.",
    ctaSub: "Pripravíme nezáväzný odhad pre váš konkrétny rozsah — reťazce, počet predajní, frekvenciu návštev.",
    ctaButton: "Kontaktujte nás",
  },
};

export default function About() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.cs;
  return (
    <div data-testid="about-page">
      <Seo
        title={{ cs: "O nás · Rodinná česká firma od 1998", en: "About · Czech family business since 1998" }}
        description={{
          cs:
            "Rodinná česká firma založená 1998. 28 let na trhu, vlastní platformy MERVIK, SYMES a QlikView. Pokrytí CZ + SK, 1 200+ prodejen.",
          en:
            "Czech family business founded in 1998. 28 years on the market with proprietary MERVIK, SYMES and QlikView platforms. CZ + SK, 1,200+ stores.",
        }}
        path="/about"
      />
      <section className="relative bg-white border-b border-[#e5e5e5]">
        <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 relative">
          <span className="gm-eyebrow text-[#0A4DFF]">{c.eyebrow}</span>
          {c.badge && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a0a0a] text-white">
              <span className="w-1.5 h-1.5 bg-[#0A4DFF]" />
              <span className="font-mono text-[11px] tracking-wider uppercase">{c.badge}</span>
            </div>
          )}
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
            {c.title}
          </h1>
          <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{c.lead}</p>
          <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
            {c.body}
          </p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-[#e5e5e5]">
            {c.stats.map((s) => (
              <div key={s.v} className="border-b md:border-b-0 md:border-r border-[#e5e5e5] py-7 pr-6 last:border-r-0">
                <div className="font-display text-3xl md:text-4xl tracking-tight">{s.k}</div>
                <div className="text-xs text-[#525252] uppercase tracking-widest mt-2 font-mono">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <span className="font-mono uppercase tracking-[0.22em] text-[13px] font-medium text-[#5B82FF]">{c.why.eyebrow}</span>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{c.why.title}</h2>
            </div>
            <p className="md:col-span-5 text-white/70 leading-relaxed">{c.why.intro}</p>
          </div>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 border-t border-white/15 pt-6">
            {c.why.points.map((p, i) => (
              <li key={i} className="flex items-start gap-4 py-4 border-b border-white/10 text-base leading-relaxed">
                <span className="font-mono text-[#5B82FF] text-xs mt-1 shrink-0">{`0${i + 1}`}</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium leading-[1.05] max-w-2xl">
            {c.valuesT}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {c.values.map((v, i) => (
              <div key={i} className="border-t-2 border-[#0a0a0a] pt-5">
                <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`V.0${i + 1}`}</span>
                <h3 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{v.h}</h3>
                <p className="text-sm text-[#525252] mt-3 leading-relaxed">{v.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white border-t border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.95]">{c.ctaTitle}</h2>
          <p className="text-[#525252] mt-5 leading-relaxed max-w-2xl">{c.ctaSub}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/kontakt" className="gm-btn gm-btn-primary" data-testid="about-cta-contact">
              {c.ctaButton} <ArrowRight size={16} />
            </Link>
            <a href="mailto:info@globalmarketing.cz" className="gm-btn gm-btn-outline">
              info@globalmarketing.cz
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
